import { ProductVisualizationUploadMessageType } from "@redotech/redo-model/conversation/message-parsers/product-visualization-upload-message-parser";
import { memo, useState } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import { ChatContentRenderer } from "./chat-content-renderer";
import { GenericRejectedState } from "./chat-content-states";

export interface HandleVisualizationUploadFn {
  (params: {
    conversationId: string;
    messageId: string;
    imageUrl: string;
    imageHash: string;
    imageExtension: string;
    pendingToolCallId?: string;
  }): Promise<void>;
}

export interface UploadVisualizationImageFn {
  (params: {
    conversationId: string;
    toolCallId: string;
    imageData: string;
    contentType: string;
  }): Promise<{ imageUrl: string; imageHash: string; imageExtension: string }>;
}

export const ProductVisualizationUpload = memo(
  function ProductVisualizationUpload({
    message,
    readOnly,
    onImageUpload,
    conversationId,
    uploadImage,
  }: {
    message: ProductVisualizationUploadMessageType;
    readOnly: boolean;
    onImageUpload?: HandleVisualizationUploadFn;
    conversationId: string;
    uploadImage: UploadVisualizationImageFn;
  }) {
    const [uploadPhase, setUploadPhase] = useState<
      "uploading" | "generating" | null
    >(null);
    const [error, setError] = useState<string | null>(null);

    const handleFileSelect = async (file: File) => {
      if (!onImageUpload || readOnly) return;

      setUploadPhase("uploading");
      setError(null);

      try {
        const base64Data = await fileToBase64(file);
        const { imageUrl, imageHash, imageExtension } = await uploadImage({
          conversationId,
          toolCallId: message.pendingToolCallId ?? "",
          imageData: base64Data,
          contentType: file.type,
        });

        setUploadPhase("generating");

        // Confirm the tool call with the uploaded image hash and extension
        await onImageUpload({
          conversationId,
          messageId: message._id,
          imageUrl,
          imageHash,
          imageExtension,
          pendingToolCallId: message.pendingToolCallId ?? undefined,
        });
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to upload image");
      } finally {
        setUploadPhase(null);
      }
    };

    return (
      <ChatContentRenderer
        contentByState={{
          pending: (
            <ImageUploadInput
              error={error}
              onFileSelect={handleFileSelect}
              readOnly={readOnly}
              uploadPhase={uploadPhase}
            />
          ),
          confirmed: null,
          rejected: (
            <GenericRejectedState title="Unable to generate visualization" />
          ),
        }}
        message={message}
      />
    );
  },
);

const ImageUploadInput = memo(function ImageUploadInput({
  onFileSelect,
  uploadPhase,
  error,
  readOnly,
}: {
  onFileSelect: (file: File) => void;
  uploadPhase: "uploading" | "generating" | null;
  error: string | null;
  readOnly: boolean;
}) {
  const [dragActive, setDragActive] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file: File) => {
    // Clear any previous validation errors
    setValidationError(null);

    // Validate file type
    const validTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp"];
    if (!validTypes.includes(file.type)) {
      setValidationError(
        `Invalid file type. Please upload ${validTypes.join(", ")}`,
      );
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setValidationError("File too large. Maximum size is 10MB");
      return;
    }

    onFileSelect(file);
  };

  if (readOnly) {
    return (
      <Flex dir="column" gap="xs">
        <Text fontSize="sm">Upload your photo to see the visualization</Text>
      </Flex>
    );
  }

  return (
    <Flex dir="column" gap="sm" style={{ width: "100%" }}>
      <label
        htmlFor="file-upload"
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        style={{
          border: dragActive
            ? "2px dashed #4A90E2"
            : "2px dashed var(--neutral-400)",
          borderRadius: "8px",
          padding: "24px",
          textAlign: "center",
          cursor: uploadPhase ? "not-allowed" : "pointer",
          backgroundColor: dragActive
            ? "var(--neutral-100)"
            : "var(--neutral-50)",
          transition: "all 0.2s ease",
        }}
      >
        <input
          accept="image/png,image/jpeg,image/jpg,image/webp"
          disabled={!!uploadPhase}
          id="file-upload"
          onChange={handleChange}
          style={{ display: "none" }}
          type="file"
        />
        <Flex align="center" dir="column" gap="xs">
          {uploadPhase ? (
            <Text fontSize="sm" textColor="tertiary">
              {uploadPhase === "uploading" ? "Uploading..." : "Generating..."}
            </Text>
          ) : (
            <>
              <Text fontSize="sm" fontWeight="medium" textColor="secondary">
                Drag & drop your photo here
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                or click to browse (PNG, JPG, WEBP • Max 10MB)
              </Text>
              <Text fontSize="xs" textColor="tertiary">
                Your image will be automatically deleted after 30 days
              </Text>
            </>
          )}
        </Flex>
      </label>
      {(error || validationError) && (
        <Text fontSize="xs" textColor="error">
          {validationError || error}
        </Text>
      )}
    </Flex>
  );
});

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // Remove the data URI prefix (e.g., "data:image/png;base64,")
      const base64 = result.split(",")[1];
      if (base64) {
        resolve(base64);
      } else {
        reject(new Error("Failed to convert file to base64"));
      }
    };
    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
}
